import React, { useState } from 'react';
import { useAuth } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowRight, ArrowLeft, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectUrl = searchParams.get('redirect');
  const isInviteRedirect = redirectUrl && redirectUrl.startsWith('/join/');

  const googleLogin = () => {
    if (redirectUrl) {
      sessionStorage.setItem('authRedirect', redirectUrl);
    }
    const callbackUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callbackUrl)}`;
  };

  const handleSubmit = async (type) => {
    setLoading(true);
    try {
      if (type === 'login') {
        await login(email, password);
      } else {
        await register(email, password);
      }
      await new Promise((r) => setTimeout(r, 100));
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate('/dashboard');
      }
    } catch (e) {
      toast.error('Authentication failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAF9F5] text-[#1A1A1A] relative">
      {/* Back link */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-sm text-[#5F5F5F] hover:text-[#1A1A1A] transition"
        data-testid="auth-back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="min-h-screen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Branding */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-lg overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png"
                  alt="Monk Mode"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-serif text-2xl tracking-tight">Monk Mode</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl leading-[1.1] tracking-[-0.02em]">
              Welcome back.
            </h1>
            <p className="mt-3 text-[#5F5F5F]">
              {isInviteRedirect
                ? 'Sign in to accept your shared-goal invite.'
                : 'Pick up where you left off.'}
            </p>
          </div>

          {isInviteRedirect && (
            <div className="mb-6 p-4 sl-card border-[#F5D5CB] bg-[#FDF0EC]">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-coral" />
                <div>
                  <p className="font-medium text-sm">Shared Goal Invite</p>
                  <p className="text-xs text-[#5F5F5F]">Sign in to accept</p>
                </div>
              </div>
            </div>
          )}

          <div className="sl-card p-8">
            {/* Google */}
            <button
              onClick={googleLogin}
              className="w-full sl-pill-ghost py-3 mb-5"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[#EEEAE0]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider">
                <span className="bg-white px-3 text-[#9E8778] font-mono">or email</span>
              </div>
            </div>

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid grid-cols-2 w-full bg-[#F4F3EE] border border-[#E5E2DA] p-1 rounded-full">
                <TabsTrigger
                  value="login"
                  className="rounded-full text-sm data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A] data-[state=active]:shadow-sm text-[#5F5F5F]"
                >
                  Sign in
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  className="rounded-full text-sm data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A] data-[state=active]:shadow-sm text-[#5F5F5F]"
                >
                  Create account
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4 mt-6">
                <AuthInput value={email} onChange={setEmail} placeholder="you@example.com" type="email" testId="login-email" />
                <AuthInput value={password} onChange={setPassword} placeholder="Password" type="password" testId="login-password" />
                <button
                  className="sl-pill-dark w-full"
                  onClick={() => handleSubmit('login')}
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in…' : 'Sign in'} <ArrowRight className="w-4 h-4" />
                </button>
              </TabsContent>

              <TabsContent value="register" className="space-y-4 mt-6">
                <AuthInput value={email} onChange={setEmail} placeholder="you@example.com" type="email" testId="register-email" />
                <AuthInput value={password} onChange={setPassword} placeholder="Password" type="password" testId="register-password" />
                <button
                  className="sl-pill-dark w-full"
                  onClick={() => handleSubmit('register')}
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? 'Creating account…' : 'Create account'} <ArrowRight className="w-4 h-4" />
                </button>
              </TabsContent>
            </Tabs>

            <p className="text-center text-xs mt-6 text-[#9E8778]">
              By continuing, you agree to our{' '}
              <a href="/terms" className="underline underline-offset-2 text-[#5F5F5F] hover:text-[#1A1A1A]" data-testid="auth-terms-link">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="underline underline-offset-2 text-[#5F5F5F] hover:text-[#1A1A1A]" data-testid="auth-privacy-link">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const AuthInput = ({ value, onChange, placeholder, type, testId }) => (
  <Input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-white border border-[#E5E2DA] rounded-xl h-12 px-4 text-[#1A1A1A] placeholder:text-[#9E9E9E] focus-visible:ring-2 focus-visible:ring-[#1A1A1A]/10 focus-visible:border-[#1A1A1A] focus-visible:ring-offset-0"
    data-testid={testId}
  />
);
